/**
 * Come un corpo di richiesta o di risposta entra nel diario tecnico senza portarsi dietro chi lo ha
 * scritto. Il diario viaggia dentro una segnalazione e esce dal browser (CLAUDE.md §6): il corpo serve a
 * capire una chiamata — quali campi c'erano, quali mancavano, cosa ha risposto il server — ma il suo
 * contenuto non serve a nessuno fuori di qui.
 *
 * La regola e' al contrario di una lista di cose da nascondere: passa solo cio' che **non puo'** essere di
 * qualcuno. Un numero, un vero/falso, un identificativo, un codice di stato, un nome di lingua. Tutto il
 * resto — una frase, un indirizzo di posta, un testo lungo — diventa `***` con la propria lunghezza, che
 * e' l'unica cosa che di quel valore serve sapere («il campo c'era ed era lungo 412 caratteri»).
 */

/** Il posto del valore che non si scrive: il campo resta, il contenuto no. */
const REDACTED = '***';

/**
 * I nomi il cui valore non entra mai, quale che sia la forma: anche un identificativo ben formato, se il
 * campo si chiama cosi', e' un segreto o e' una persona. Il confronto e' per sotto-stringa in minuscolo,
 * cosi' `accessToken`, `id_token` e `TOKEN` cadono sotto la stessa voce.
 */
const REDACTED_KEYS: readonly string[] = [
  'token',
  'password',
  'secret',
  'signature',
  'sas',
  'otp',
  'pin',
  'credential',
  'authorization',
  'cookie',
  'email',
  'mail',
  'phone',
  'tel',
  'name',
  'surname',
  'fiscal',
  'address',
  'text',
  'message',
  'content',
  'transcript',
  'note',
  'comment',
  'query',
  'prompt',
  'answer',
  'question',
  'label',
  'title',
  'summary',
  'description',
];

/**
 * Un valore di testo si scrive solo se non puo' essere una parola di qualcuno: nessuno spazio, corto, e
 * fatto dei caratteri con cui si scrivono identificativi, codici e nomi tecnici. `f2b1…-9c`, `it-IT`,
 * `citizen`, `2026-09-21T10:00:00Z` passano; «Buongiorno, ho un problema» no.
 */
const SAFE_TEXT = /^[\w.:+/@-]{1,64}$/;

/** Un indirizzo di posta passerebbe da `SAFE_TEXT` — la chiocciola serve agli identificativi — quindi si toglie a parte. */
const EMAIL_SHAPED = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Quanto in profondita' si scende in un corpo annidato prima di scrivere solo la forma di cio' che resta. */
const MAX_DEPTH = 4;

/** Quanti elementi di una lista si scrivono: di una lista lunga interessa il primo elemento e quanti sono. */
const MAX_ARRAY_ITEMS = 5;

/** Quanti caratteri di corpo entrano nel diario. Il budget della segnalazione e' 30.000 in tutto: un corpo che se ne prende un decimo cancellerebbe la storia che lo precede. */
export const TECHNICAL_LOG_PAYLOAD_MAX_LENGTH = 700;

/**
 * Il corpo come si scrive nel diario: la sua forma, coi valori che si possono scrivere e un `***` al posto
 * degli altri. `undefined` quando non c'e' corpo — una riga in meno da leggere invece di un `req=null`.
 */
export function describePayload(
  body: unknown,
  maxLength = TECHNICAL_LOG_PAYLOAD_MAX_LENGTH,
): string | undefined {
  if (body === undefined || body === null) return undefined;

  const described = describeValue(parsedIfJson(body), 0);
  const text = typeof described === 'string' ? described : JSON.stringify(described);

  if (text === undefined) return undefined;

  return truncate(text, maxLength);
}

/**
 * Un corpo gia' serializzato dal chiamante si riapre prima di descriverlo: come testo sarebbe una riga
 * lunga e opaca — quindi `***` — mentre come dati se ne salvano i campi che si possono scrivere.
 */
function parsedIfJson(body: unknown): unknown {
  if (typeof body !== 'string') return body;

  const trimmed = body.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return body;

  try {
    return JSON.parse(trimmed);
  } catch {
    return body;
  }
}

/** Un nome sensibile, in qualunque involucro lo si scriva: `accessToken`, `id_token`, `TOKEN`. */
function isRedactedKey(key: string): boolean {
  const lowered = key.toLowerCase();

  return REDACTED_KEYS.some((redactedKey) => lowered.includes(redactedKey));
}

/**
 * I corpi che non sono dati ma byte — un audio registrato, un allegato scelto, un modulo con un file — si
 * dichiarano per quello che sono: quanto pesano e di che tipo sono. Il loro contenuto e' proprio cio' che
 * non deve uscire, e dentro un diario di testo non entrerebbe comunque.
 */
function describeValue(value: unknown, depth: number): unknown {
  if (value === null) return null;

  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') return describeText(value);

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return `[blob ${value.size}B ${value.type || 'sconosciuto'}]`;
  }
  if (typeof FormData !== 'undefined' && value instanceof FormData) return describeFormData(value);
  if (value instanceof ArrayBuffer) return `[binario ${value.byteLength}B]`;
  if (ArrayBuffer.isView(value)) return `[binario ${value.byteLength}B]`;
  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value)) return describeArray(value, depth);
  if (typeof value === 'object') return describeObject(value as Record<string, unknown>, depth);

  // Una funzione, un simbolo: non sono dati di una chiamata e non hanno una forma da scrivere.
  return undefined;
}

/** Di una lista contano i primi elementi e quanti sono: una lista di cento suggerimenti si spiega coi primi. */
function describeArray(values: readonly unknown[], depth: number): unknown {
  if (depth >= MAX_DEPTH) return `[lista ${values.length}]`;

  const kept = values.slice(0, MAX_ARRAY_ITEMS).map((value) => describeValue(value, depth + 1));

  return values.length > MAX_ARRAY_ITEMS ? [...kept, `+${values.length - MAX_ARRAY_ITEMS}`] : kept;
}

function describeObject(value: Record<string, unknown>, depth: number): unknown {
  if (depth >= MAX_DEPTH) return `[oggetto ${Object.keys(value).length} campi]`;

  const described: Record<string, unknown> = {};

  for (const [key, item] of Object.entries(value)) {
    if (item === undefined) continue;
    described[key] = isRedactedKey(key) ? redacted(item) : describeValue(item, depth + 1);
  }

  return described;
}

/**
 * Di un modulo si scrivono i nomi dei campi e mai i valori: un `multipart` porta il file scelto e i dati
 * che lo accompagnano, e li porta gia' tutti sotto un nome che li descrive.
 */
function describeFormData(form: FormData): string {
  const names: string[] = [];
  form.forEach((_value, name) => names.push(name));

  return `[form ${names.join(',') || 'vuoto'}]`;
}

/**
 * Il testo che si puo' scrivere e quello che diventa `***`. La lunghezza resta sempre: dice se il campo
 * era pieno o vuoto, che e' meta' della diagnosi di una chiamata andata storta.
 */
function describeText(value: string): string {
  if (value.length === 0) return '';
  if (EMAIL_SHAPED.test(value)) return redactedText(value.length);

  return SAFE_TEXT.test(value) ? value : redactedText(value.length);
}

/** Un campo dal nome sensibile: non si guarda cosa contiene, si dice solo se c'era qualcosa. */
function redacted(value: unknown): string {
  if (typeof value === 'string') return redactedText(value.length);

  return REDACTED;
}

function redactedText(length: number): string {
  return `${REDACTED}(${length})`;
}

/** Il taglio dice di essere un taglio: un corpo mozzato senza dirlo si legge come un corpo malformato. */
function truncate(text: string, maxLength: number): string {
  return text.length <= maxLength
    ? text
    : `${text.slice(0, maxLength)}…[+${text.length - maxLength}]`;
}
